import type { TerminalSubjectKind } from '@developerz/domain';
import { createEffect, createMemo, createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { z } from 'zod';
import { t } from '../../i18n';
import { readLocalPref, writeLocalPref } from '../local-pref';
import { STATUS_CELL_KIND, type TerminalStatusCell } from '../terminal-status-view';
import type { BufferRow } from './buffer';
import {
  closePane,
  createTerminalLayout,
  normalizeLoadedLayout,
  paneIds,
  setFocus,
  setRatioAtPath,
  splitPane,
  type TerminalLayout,
  TerminalLayoutSchema,
  toggleZoom,
} from './layout';

// The pane tree's STATEFUL half (#4397, epic #4391): the layout signal, its
// `localStorage` persistence, and the per-pane filter/kind-cell state —
// split out of `Terminal.tsx` itself, which was over `scripts/size-budget.ts`'s
// 120-line function ceiling once this and the pane tree's JSX lived in one
// function. `layout.ts` stays pure (every op a plain function, unit-tested
// with no signals); this is the one place those ops meet `createSignal`.

const TERMINAL_LAYOUT_KEY = 'dz-terminal-layout';
const TERMINAL_VERBOSE_KEY = 'dz-terminal-verbose';
const TERMINAL_TIME_KEY = 'dz-terminal-time';
const INITIAL_PANE_ID = 'p1';

/** Per-pane, keyed by pane id — an id with no entry reads as `false` (audit-only),
 *  matching every other per-pane store here (`paneFilterText`/`paneStatusCell`). */
const PaneVerboseSchema = z.record(z.string(), z.boolean());

/** Whether a pane pins to the newest row (#4398) — see `followFor`. */
type FollowMode = 'following' | 'paused';

/** How every row's timestamp reads (#4401): wall clock (`14:02:11`) or age (`2 minutes ago`). */
export type TerminalTimeMode = 'clock' | 'relative';
const TimeModeSchema = z.enum(['clock', 'relative']);

export type TerminalPanes = {
  readonly layout: () => TerminalLayout;
  /** The focused pane's id: what the status line and the command line act on. */
  readonly focusedId: () => string;
  readonly filterTextFor: (id: string) => string;
  /** The `subject.kind` this pane's pressed status cell narrows it to, derived from {@link statusCellFor}. */
  readonly kindFilterFor: (id: string) => TerminalSubjectKind | null;
  readonly setFilterText: (id: string, text: string) => void;
  /**
   * The status-line cell this pane has pressed (plan 101 slice 19 row 19-14).
   * Held HERE, per pane, so it survives a focus switch: the status line shows
   * the focused pane's own cell and owns no state of its own.
   */
  readonly statusCellFor: (id: string) => TerminalStatusCell | null;
  /** Press `cell` for this pane, or release it if it is the one pressed. */
  readonly toggleStatusCell: (id: string, cell: TerminalStatusCell) => void;
  /** This pane's own verbose toggle (#4399 slice 08) — persisted across reload
   *  under {@link TERMINAL_VERBOSE_KEY}, same posture as the layout above. */
  readonly verboseFor: (id: string) => boolean;
  readonly setVerbose: (id: string, on: boolean) => void;
  /**
   * Whether ANY pane currently in the layout wants verbose. There is exactly
   * ONE live connection for the whole window (`terminal-stream.ts`'s "one
   * connection per tab" — every pane is a filter over the SAME shared
   * buffer, never a second dial), so the wire-level `?verbose=` the stream
   * actually requests is the OR of every pane's own toggle: turning verbose
   * on in one pane widens what the shared buffer holds, and a pane that
   * still wants audit-only filters the extra rows back out client-side
   * (`lib/terminal/filter.ts`'s `filterVerboseRows`, `TerminalPane.tsx`).
   * Derived here, not in `Terminal.tsx`, so the caller never has to reach
   * into `layout.ts`'s `paneIds` itself just to answer this.
   */
  readonly anyVerbose: () => boolean;
  /**
   * This pane's follow mode (#4398) — LIFTED out of `TerminalLog.tsx` so the
   * command line's `follow`/`pause`/`tail` drive the SAME flag the log's own
   * scroll and "N new" pill read and write. Not persisted: a reload tails
   * live again. An id with no entry is following.
   */
  readonly followFor: (id: string) => boolean;
  readonly setFollow: (id: string, on: boolean) => void;
  /**
   * The row this pane was `clear`ed at (#4398): the pane shows only rows
   * after it. A per-pane view cursor over the SHARED buffer — clearing one
   * pane never removes a row another pane shows. `null` = never cleared.
   */
  readonly clearedAtFor: (id: string) => BufferRow | null;
  readonly setClearedAt: (id: string, marker: BufferRow | null) => void;
  /**
   * A bump counter per pane (#4401): Ctrl+F or the title bar's Search button
   * bumps the focused pane's, and that pane opens (or re-focuses) its search.
   */
  readonly searchRequestFor: (id: string) => number;
  readonly requestSearch: (id: string) => void;
  /** Window-wide timestamp style — persisted under `dz-terminal-time`, like the layout. */
  readonly timeMode: () => TerminalTimeMode;
  readonly setTimeMode: (mode: TerminalTimeMode) => void;
  /** A short identity for a pane's own title bar and its log region's aria-label. */
  readonly paneLabel: (id: string) => string;
  /** True once there is more than one pane — closing the last is a no-op regardless of which id. */
  readonly canClose: () => boolean;
  readonly onFocus: (id: string) => void;
  readonly onSplitRow: (id: string) => void;
  readonly onSplitColumn: (id: string) => void;
  readonly onToggleZoom: (id: string) => void;
  readonly onClose: (id: string) => void;
  readonly onRatioChange: (path: readonly ('a' | 'b')[], ratio: number) => void;
};

export function useTerminalPanes(): TerminalPanes {
  // Persisted under `dz-terminal-layout`, GLOBAL — not org-scoped, the same
  // way the theme toggle isn't: a person's preferred split is a UI
  // preference, not account data. Re-normalized on load
  // (`normalizeLoadedLayout`): a ratio outside bounds re-clamps, and a
  // `zoomId`/`focusId` naming a pane that no longer exists (a corrupt or
  // hand-edited value) falls back exactly like a fresh operation's own
  // result would — `readLocalPref` already degrades an unparseable or
  // schema-invalid value to the single-pane fallback before this even runs.
  const [layout, setLayout] = createSignal<TerminalLayout>(
    normalizeLoadedLayout(
      readLocalPref(
        TERMINAL_LAYOUT_KEY,
        TerminalLayoutSchema,
        createTerminalLayout(INITIAL_PANE_ID),
      ),
    ),
  );
  createEffect(() => writeLocalPref(TERMINAL_LAYOUT_KEY, layout()));

  // Per-pane UI state: the filter box text and the status line's pressed
  // cell. Keyed by pane id; an id with no entry reads as "no filter", so
  // a freshly split pane starts unfiltered without either store needing to
  // be seeded for it.
  const [paneFilterText, setPaneFilterText] = createStore<Record<string, string>>({});
  const [paneStatusCell, setPaneStatusCell] = createStore<
    Record<string, TerminalStatusCell | null>
  >({});
  const filterTextFor = (id: string): string => paneFilterText[id] ?? '';
  const statusCellFor = (id: string): TerminalStatusCell | null => paneStatusCell[id] ?? null;
  const kindFilterFor = (id: string): TerminalSubjectKind | null => {
    const cell = statusCellFor(id);
    return cell === null ? null : STATUS_CELL_KIND[cell];
  };

  // Persisted (unlike the two filter stores above) — a per-pane verbose
  // toggle is closer to the layout than to a transient filter box: the click-
  // through this ships against expects it to survive a reload.
  const [paneVerbose, setPaneVerbose] = createSignal<Readonly<Record<string, boolean>>>(
    readLocalPref(TERMINAL_VERBOSE_KEY, PaneVerboseSchema, {}),
  );
  createEffect(() => writeLocalPref(TERMINAL_VERBOSE_KEY, paneVerbose()));
  const verboseFor = (id: string): boolean => paneVerbose()[id] ?? false;
  const setVerbose = (id: string, on: boolean): void => {
    setPaneVerbose((prev) => ({ ...prev, [id]: on }));
  };
  const anyVerbose = createMemo(() => paneIds(layout().root).some((id) => verboseFor(id)));

  // A MODE per pane, not a flag: `keyed-mutation.guard.test.ts` reserves the
  // `Record<string, boolean>` store for the per-row in-flight lock, and this is
  // not one. An id with no entry is following.
  const [paneFollow, setPaneFollow] = createStore<Record<string, FollowMode>>({});
  const [paneClearedAt, setPaneClearedAt] = createStore<Record<string, BufferRow | null>>({});
  const [paneSearchRequest, setPaneSearchRequest] = createStore<Record<string, number>>({});

  const [timeMode, setTimeMode] = createSignal<TerminalTimeMode>(
    readLocalPref(TERMINAL_TIME_KEY, TimeModeSchema, 'clock'),
  );
  createEffect(() => writeLocalPref(TERMINAL_TIME_KEY, timeMode()));

  function paneLabel(id: string): string {
    const text = filterTextFor(id).trim();
    const kind = kindFilterFor(id);
    if (text !== '' && kind !== null) return `${kind} ${text}`;
    if (text !== '') return text;
    if (kind !== null) return kind;
    return t('terminal.pane_unfiltered');
  }

  return {
    layout,
    focusedId: () => {
      // `layout.ts`'s `normalize` (every op and the load go through it) never
      // leaves `focusId` null; the type is nullable only for the stored shape.
      const id = layout().focusId;
      if (id === null) {
        throw new Error(
          'useTerminalPanes: layout.focusId is null; every layout must pass through normalize() in lib/terminal/layout.ts',
        );
      }
      return id;
    },
    filterTextFor,
    kindFilterFor,
    setFilterText: (id, text) => setPaneFilterText(id, text),
    statusCellFor,
    toggleStatusCell: (id, cell) => setPaneStatusCell(id, statusCellFor(id) === cell ? null : cell),
    verboseFor,
    setVerbose,
    anyVerbose,
    followFor: (id) => paneFollow[id] !== 'paused',
    setFollow: (id, on) => setPaneFollow(id, on ? 'following' : 'paused'),
    clearedAtFor: (id) => paneClearedAt[id] ?? null,
    setClearedAt: (id, marker) => setPaneClearedAt(id, marker),
    searchRequestFor: (id) => paneSearchRequest[id] ?? 0,
    requestSearch: (id) => setPaneSearchRequest(id, (n) => (n ?? 0) + 1),
    timeMode,
    setTimeMode,
    paneLabel,
    canClose: () => paneIds(layout().root).length > 1,
    onFocus: (id) => setLayout((l) => setFocus(l, id)),
    onSplitRow: (id) => setLayout((l) => splitPane(l, id, 'row', crypto.randomUUID())),
    onSplitColumn: (id) => setLayout((l) => splitPane(l, id, 'column', crypto.randomUUID())),
    onToggleZoom: (id) => setLayout((l) => toggleZoom(l, id)),
    onClose: (id) => setLayout((l) => closePane(l, id)),
    onRatioChange: (path, ratio) => setLayout((l) => setRatioAtPath(l, path, ratio)),
  };
}
